'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import type { FeatureMode } from '@/lib/directory/compare-url'
import { tw } from '@/lib/utils'

interface FeatureModeToggleProps {
  value: FeatureMode
  onChange: (value: FeatureMode) => void
}

export const FeatureModeToggle = ({ value, onChange }: FeatureModeToggleProps) => {
  const { t } = useTranslation()
  const options = [
    {
      value: 'and',
      label: t('directory.features.and', 'AND'),
      description: t('directory.features.matchAll', 'Match all selected features'),
    },
    {
      value: 'or',
      label: t('directory.features.or', 'OR'),
      description: t('directory.features.matchAny', 'Match any selected feature'),
    },
  ] as const

  return (
    <div
      role="group"
      aria-label={t('directory.features.match', 'Feature matching')}
      className="border-navy/20 inline-flex rounded border bg-white p-px"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          data-tracking="ignore"
          aria-pressed={value === option.value}
          aria-label={option.description}
          title={option.description}
          onClick={() => onChange(option.value)}
          className={tw(
            'focus-visible:outline-lapis h-5 min-w-9 cursor-pointer rounded-sm px-1 text-[11px] leading-none font-semibold focus-visible:outline-2 focus-visible:outline-offset-2',
            value === option.value ? 'bg-navy text-white' : 'text-navy hover:bg-navy/5'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
